import type { InvitationData } from '../types';
import { safeFormatDateLong } from '@/src/infrastructure/lib/weddingDataHelper';
import { PLOT_SCENES } from './cameraPath';
import { assignBeatEvents } from './planeSources';

export interface PlotPhoto {
  label: string;
  src: string;
}

export interface PlotChapter {
  id: string;
  title: string;
  line: string;
  detail: string | null;
}

export interface PlotPlaneContent {
  coupleLine: string;
  hostLine: string;
  dateLine: string;
  venueLine: string;
  mapsQuery: string | null;
  photos: PlotPhoto[];
  videoUrl: string | null;
  chapters: PlotChapter[];
}

interface LooseEvent {
  title?: string;
  startTime?: string;
  time?: string;
  venue?: string;
  date?: string;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function pickString(...values: unknown[]): string | null {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
}

function readEvents(custom: Record<string, unknown> | null, wedding: Record<string, unknown> | null): LooseEvent[] {
  const raw = wedding?.weddingEvents ?? wedding?.ceremonies ?? custom?.ceremonies;
  if (!Array.isArray(raw)) return [];
  return raw.filter((item) => item && typeof item === 'object') as LooseEvent[];
}

function detailLine(event: LooseEvent | undefined): string | null {
  if (!event) return null;
  const bits = [event.startTime || event.time, event.venue].filter(
    (bit): bit is string => typeof bit === 'string' && bit.trim().length > 0,
  );
  return bits.length > 0 ? bits.join(' · ') : null;
}

function httpsUrl(value: string | null): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    return url.toString();
  } catch {
    return null;
  }
}

export function resolvePlotPlaneContent(data: InvitationData): PlotPlaneContent {
  const custom = asRecord(data.customData);
  const wedding = asRecord(custom?.wedding);
  const expMeta = asRecord(custom?.expMeta) ?? asRecord(wedding?.expMeta);
  const groom = asRecord(wedding?.groom);
  const bride = asRecord(wedding?.bride);

  const groomName = pickString(groom?.fullName, groom?.nickname) ?? '';
  const brideName = pickString(bride?.fullName, bride?.nickname) ?? '';

  const coupleLine =
    brideName && groomName
      ? `${brideName} & ${groomName}`
      : brideName || groomName || pickString(data.eventName) || '';

  const hostLine = pickString(data.hostName) ?? 'With their families';
  const dateLine = safeFormatDateLong(data.eventDate);
  const venueLine = pickString(data.eventVenue) ?? 'Venue to be announced';
  const mapsQuery = venueLine !== 'Venue to be announced' ? venueLine : null;

  const photos: PlotPhoto[] = [];
  const bridePhoto = httpsUrl(
    pickString(bride?.photoUrl, expMeta?.bridePhoto, custom?.bridePhoto, wedding?.bridePhoto),
  );
  const groomPhoto = httpsUrl(
    pickString(groom?.photoUrl, expMeta?.groomPhoto, custom?.groomPhoto, wedding?.groomPhoto),
  );
  const couplePhoto = httpsUrl(
    pickString(expMeta?.couplePhoto, custom?.couplePhoto, wedding?.couplePhoto),
  );
  if (bridePhoto) photos.push({ label: 'Bride', src: bridePhoto });
  if (groomPhoto) photos.push({ label: 'Groom', src: groomPhoto });
  if (couplePhoto) photos.push({ label: 'Together', src: couplePhoto });

  const videoUrl = httpsUrl(
    pickString(
      custom?.customVideoUrl,
      expMeta?.customVideoUrl,
      custom?.preWeddingVideoUrl,
      wedding?.customVideoUrl,
      wedding?.preWeddingVideoUrl,
    ),
  );

  const events = readEvents(custom, wedding);
  const beatEvents = assignBeatEvents(events);
  const chapters: PlotChapter[] = PLOT_SCENES.map((scene) => ({
    id: scene.id,
    title: scene.title,
    line: scene.line,
    detail: detailLine(beatEvents.get(scene.id)),
  }));

  return { coupleLine, hostLine, dateLine, venueLine, mapsQuery, photos, videoUrl, chapters };
}

export const PLOT_PLANE_DEMO_DATA: InvitationData = {
  id: 'demo-wedding-plot-plane',
  templateId: 'exp-wedding-plot-plane-3d',
  occasionType: 'WEDDING',
  status: 'DRAFT',
  eventName: 'Aisha & Kabir',
  eventDate: '2026-12-12T13:00:00.000Z',
  eventVenue: 'The Fern Courtyard, Udaipur',
  hostName: 'The Malhotra and Kapoor families',
  customMessage: 'Join us for every colour of this wedding.',
  customData: {
    wedding: {
      bride: { fullName: 'Aisha' },
      groom: { fullName: 'Kabir' },
      weddingEvents: [
        { title: 'Sangeet', date: '2026-12-11', startTime: '7:00 PM', venue: 'The Fern Lawns, Udaipur' },
        { title: 'Mehndi', date: '2026-12-11', startTime: '4:00 PM', venue: 'Courtyard Garden' },
        { title: 'Ring Ceremony', date: '2026-12-12', startTime: '11:00 AM', venue: 'The Fern Courtyard, Udaipur' },
        { title: 'Havan', date: '2026-12-12', startTime: '6:30 PM', venue: 'The Fern Mandap, Udaipur' },
        { title: 'Saat Pheras', date: '2026-12-12', startTime: '7:15 PM', venue: 'The Fern Mandap, Udaipur' },
      ],
    },
  },
};
